import React, { useRef, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Avatar,
  Tooltip,
  Card,
  CardMedia,
  CardHeader,
  CardContent,
  CardActions,
  useMediaQuery,
  TextField,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import Avatar3 from "./assets/SearchResults/Avatars/Robert_Thompson.png";

import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";
import AlertPopup from "./components/AlertPopup";
import EmptyState from "./components/EmptyState";
//import UserCard from "./components/UserCard";
import FollowButton from "./components/FollowButton";
import FloatingMenu from "./components/FloatingMenu";
import ProductCard from "./components/ProductCard";

import { COLLECTIONS_BY_USER_UUID } from "./graphQL/collection_qry";

import placeholderImage from "./assets/r3play-logo-sml.png";

export default function MyCollection() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const navigate = useNavigate();
  const [contentType, setContentType] = useState("All");
  const [products, setProducts] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);  
  
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [alertPopup, setAlertPopup] = useState({
    open: false,
    productId: null,
    productTitle: "",
  });

  const typeMap = {
      toy: "Toys",
      video: "Videos",
      story: "Stories",
  };

  let userUuid = localStorage.getItem("userUuid") ? localStorage.getItem("userUuid") : null;

  const loggedUserName = localStorage.getItem("userName") || "";

  const [getCollectionsData] = useManualQuery(
    COLLECTIONS_BY_USER_UUID, {
    fetchPolicy: "network-only",
  });

  // Set floating menu place based on screen size
  useEffect(() => {
    // const leftMargin = isMobile ? 195 : isTablet ? 200 : 270;
    const handleResize = () => {
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);

  // Helper function to get video thumbnail from any platform
  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return "";

    // YouTube
    if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
      let videoId = "";
      if (videoUrl.includes("youtu.be/")) {
        videoId = videoUrl.split("youtu.be/")[1].split("?")[0];
      } else if (videoUrl.includes("youtube.com")) {
        const urlParams = new URLSearchParams(videoUrl.split("?")[1]);
        videoId = urlParams.get("v");
      }
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }

    // Vimeo
    if (videoUrl.includes("vimeo.com")) {
      const videoId = videoUrl.split("/").pop().split("?")[0];
      return `https://vumbnail.com/${videoId}.jpg`;
    }

    // Dailymotion
    if (videoUrl.includes("dailymotion.com") || videoUrl.includes("dai.ly")) {
      let videoId = "";
      if (videoUrl.includes("dai.ly/")) {
        videoId = videoUrl.split("dai.ly/")[1].split("?")[0];
      } else {
        videoId = videoUrl.split("/video/")[1].split("?")[0];
      }
      return `https://www.dailymotion.com/thumbnail/video/${videoId}`;
    }

    // Default fallback - return empty or a placeholder
    return "";
  };

 const handleUserProfileNavigation = (user) => {
    // Navigate to user's public homepage
    navigate(`/user/${user.userUuid}/profile`);
  };


  const setCollectionList = async () => {    
    if (!userUuid) return;
    const {
      loading: loadingCollectionListData,
      error: errorCollectionListData,
      data: collectionListData,
    } = await getCollectionsData({
      variables: {
        userUuid: userUuid,
      },
    });
    
    if (!loadingCollectionListData && collectionListData && collectionListData.collectionsByUseruuid) { 
                  
      setProducts(
        collectionListData.collectionsByUseruuid
        .filter(
            item => item?.objectCardInfo
          )
         .map(item => ({
          id: item.collectionId,
          userId: {
            userId: item.objectCardInfo.ownerInfo.id,
            userUuid: item.objectCardInfo.ownerInfo.userUuid,
            firstName: item.objectCardInfo.ownerInfo.firstName ,
            lastName: item.objectCardInfo.ownerInfo.lastName,
            username: item.objectCardInfo.ownerInfo.username,            
            avatarFileName: item.objectCardInfo.ownerInfo.avatarFileName,
            isFollowing: item.objectCardInfo.ownerInfo.isLoggedInUserFollowing,
            listings: item.objectCardInfo.ownerInfo.listings,
            soldToys: item.objectCardInfo.ownerInfo.soldToys,
            ratingNo: item.objectCardInfo.ownerInfo.ratingNo,
            ratingCount: item.objectCardInfo.ownerInfo.ratingCount,
            dateJoined: item.objectCardInfo.ownerInfo.dateJoined,
          },
          uuid: item.objectCardInfo.uuid,
          title: item.objectCardInfo.title,
          price: item.objectCardInfo.sellingPrice,
          oldPrice: item.objectCardInfo.originalPrice,
          category: item.objectCardInfo.category,
          contentType: typeMap[item.contentType.model] || "",
          shareUrl: `${window.location.origin}${item.objectCardInfo.shareUrl}`,
          img: item.contentType.model === 'video' ? getVideoThumbnail(item.objectCardInfo.image) || placeholderImage 
               : item.objectCardInfo.image,

          likes: item.objectCardInfo.likeNo,
          isLiked: item.objectCardInfo.isLiked,
          shares: item.objectCardInfo.shareNo,
          isInCollection: true,
          content: item.objectCardInfo.content,
          commentNo: item.objectCardInfo.commentNo,
          comments: item.objectCardInfo.comments,
        }))        
      ); 
      setIsLoaded(true); 
    } else {
      console.log("errorCollectionListData: ", errorCollectionListData);           
    };
  };

  useEffect(() => {
    (async () => setCollectionList())();
  }, [userUuid]);

  const handleUserFollowUpdate = () => {
    // Update user data in products state
    setCollectionList()
    
  };  

  const handleContentTypeChange = (event) => {
    setContentType(event.target.value);
  };

  // Filter products based on content type
  const getFilteredProducts = () => {
    if (contentType === "All") {
      return products;
    }
    return products.filter((product) => product.contentType === contentType);
  };

  const filteredProducts = getFilteredProducts();

  // Handle bookmark click to show confirmation popup
  const handleBookmarkClick = (productId) => {
    const product = products.find((p) => p.id === productId);
    setAlertPopup({
      open: true,
      productId: productId,
      productTitle: product ? product.title : "",
    });
  };  

  // Handle share click to increment share count
  const handleShareClick = (productId) => {
    setProducts((prev) =>
      prev.map((product) =>
        product.id === productId
          ? { ...product, shares: (product.shares || 0) + 1 }
          : product
      )
    );
  };

  // Handle confirm removal
  const handleConfirmRemoval = () => {
    setProducts((prevProducts) =>
      prevProducts.filter((product) => product.id !== alertPopup.productId)
    );
    setAlertPopup({ open: false, productId: null, productTitle: "" });
  };

  // Handle cancel removal
  const handleCancelRemoval = () => {
    setAlertPopup({ open: false, productId: null, productTitle: "" });
  };

  return (
    <>
      <Helmet>
        <title>R3PLAY - My Collection</title>
        <meta name="description" content="Browse your toy collection" />
      </Helmet>

      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          pt: 3,
          pb: 4,
        }}
      >
        <Box
          sx={{
            px: "100px",
            "@media (max-width: 1360px)": {
              px: "16px",
            },
            "@media (max-width: 856px)": {
              px: "16px",
            },
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              p: 2,
              pl: 2,
              pr: 2,
              mb: 3,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                "@media (max-width: 833px)": {
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 2,
                },
              }}
            >
              {/* Title Section */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "30px",
                    fontWeight: "bold",
                    color: "#333",
                  }}
                >
                  My Collection
                </Typography>
                <Typography
                  sx={{
                    fontSize: "16px",
                    color: "#666",
                  }}
                >
                  Your personal space for everything you've collected.
                </Typography>
              </Box>

              {/* Content Type Filter */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  "@media (max-width: 833px)": {
                    width: "100%",
                    justifyContent: "flex-start",
                  },
                }}
              >
                <TextField
                  select
                  label="Content Type"
                  value={contentType}
                  onChange={handleContentTypeChange}
                  sx={{
                    width: "150px",
                    "& .MuiOutlinedInput-root": {
                      height: "40px",
                      backgroundColor: "white",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#e0e0e0",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "#666",
                      fontSize: "14px",
                      "&.Mui-focused": {
                        color: "#666",
                      },
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                  }}
                  SelectProps={{
                    IconComponent: KeyboardArrowDownIcon,
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Toys">Toys</MenuItem>
                  <MenuItem value="Stories">Stories</MenuItem>
                  <MenuItem value="Videos">Videos</MenuItem>
                </TextField>
              </Box>
            </Box>
          </Box>

          {!isLoaded ? (
            <Box p={4} textAlign="center">Loading...</Box>
              ) 
              : isLoaded && filteredProducts.length === 0 ? 
              (
                <EmptyState 
                  type="listings"
                  title="No Collections Available"
                  description="You haven't added any items to your collection yet."
                />
              ) : (
              <>

                {/* Products Grid */}
                <Box
                  sx={{
                    maxWidth: "1800px",
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    "@media (max-width: 833px)": {
                      justifyContent: "center",
                    },
                    gap: "24px",
                    width: "100%",
                    pt: "1px",
                  }}
                >          
                {filteredProducts.map((product) => {
                  return (
                    <Box
                      key={product.id}
                      sx={{
                        width: "288px",
                        position: "relative",
                        overflow: "visible",
                        "@media (max-width: 856px)": {
                          mb: "24px",
                        },
                      }}
                    >
                      <ProductCard
                        p={product}
                        onBookmarkClick={handleBookmarkClick}
                        onUserProfileNavigation={handleUserProfileNavigation}
                        onFollowToggle={handleUserFollowUpdate}
                        onShareClick={handleShareClick}
                      />
                    </Box>
                  );
                })}
              
          </Box>
          </>
            )}
        </Box>
      </Box>

      {/* Alert Popup for removal confirmation */}
      <AlertPopup
        open={alertPopup.open}
        title={`Are you sure you want to remove from your collection the item with title "${alertPopup.productTitle}"?`}
        message=""
        confirmText="Yes, Remove"
        cancelText="Cancel"
        onConfirm={handleConfirmRemoval}
        onCancel={handleCancelRemoval}
        onClose={handleCancelRemoval}
      />      
      <FloatingMenu left={floatMenuLeftMargin} />
    </>
  );
}
